/**
 * Vocal-mode UI for scale-degree exercises.
 *
 * Sits alongside (never replaces) the SolfegeButtons. When the user
 * clicks "Sing the answer", we:
 *
 *   1. Play a short tonic reference through the existing audio engine
 *      (anchors the user's relative pitch — important after a cadence
 *      that ended on the leading tone, etc.).
 *   2. Lazy-init a per-mount PitchDetector; request mic on the first
 *      use only.
 *   3. Show a live readout of the most recent stable held pitch
 *      (intentionally minimal — a single line like "you sang: re ±5¢"
 *      rather than a pitch ribbon, per the spec brief's "minimal
 *      readout is defensible if the ribbon ends up sprawling").
 *   4. On detectHeldPitch + gradeScaleDegree → fire onAnswer with the
 *      matching option ID (auto-submit).
 *   5. After a 5-second silent window, surface the spec §9.4 message
 *      and a Try Again button.
 *
 * The detector is destroyed on unmount and on exercise change so
 * subsequent vocal answers never see stale events from a prior key.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPitchDetector, type PitchDetector, type PitchEvent } from "@music-app/input-mic";
import type { AudioPlayer } from "@music-app/audio-engine-web";
import { AudioPlanBuilder } from "@music-app/exercise-generator";
import type { Exercise } from "@music-app/exercise-generator";
import { gradeVocalScaleDegree } from "./vocal-grading.js";

/**
 * Bundled URL of the input-mic AudioWorklet. The `?worker&url` query
 * tells Vite: take the worklet's TypeScript source, compile it, bundle
 * its imports (pitchy, the local PitchAnalyzer + types), and emit a
 * single ES-module asset; return the hashed asset URL. `worker.format:
 * "es"` in vite.config.ts is what makes the emitted file an ES module
 * — the default IIFE wrapper would break `audioWorklet.addModule()`.
 *
 * This is the Vite-specific seam the input-mic package's
 * `pitchWorkletUrl()` helper documents as the recommended consumer
 * pattern. The mobile sub-track in Phase 2B Block F won't use Vite
 * (it uses Metro) and will resolve its own worklet URL via the same
 * `init({ workletUrl })` injection point.
 */
import pitchWorkletAssetUrl from "@music-app/input-mic/pitch-worklet?worker&url";

const SOLFEGE_MAJOR = ["do", "re", "mi", "fa", "sol", "la", "ti"];
const SOLFEGE_MINOR = ["la", "ti", "do", "re", "mi", "fa", "sol"];

/** Window of recent PitchEvents to keep around for held-pitch detection.
 *  At ~50 fps and a 200 ms hold, ~30 events is comfortable headroom. */
const EVENT_WINDOW = 60;

/** No-pitch timeout (spec §9.4). */
const SILENCE_TIMEOUT_MS = 5000;

/** Reference tonic playback duration. Long enough for a beat of pitch
 *  anchoring without dragging the flow out. */
const TONIC_REF_SECONDS = 1.0;

export interface VocalSingerProps {
  exercise: Exercise;
  audioPlayer: AudioPlayer | null;
  /** True when Practice's machine is in `awaiting` (post-playback,
   *  pre-answer). Disables the button outside that window. */
  enabled: boolean;
  /** Called with the auto-graded option ID once a stable scale tone is detected. */
  onAnswer: (optionId: string) => void;
}

type Phase =
  | "idle"
  | "playing-reference"
  | "listening"
  | "matched"
  | "timeout"
  | "denied"
  | "error";

export function VocalSinger({
  exercise,
  audioPlayer,
  enabled,
  onAnswer,
}: VocalSingerProps): ReactNode {
  const [phase, setPhase] = useState<Phase>("idle");
  const [readout, setReadout] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const detectorRef = useRef<PitchDetector | null>(null);
  const eventsRef = useRef<PitchEvent[]>([]);
  const submittedRef = useRef(false);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const tonicMidi = exercise.meta.tonicMidi;
  const mode = exercise.meta.tonalityMode;

  /** Tear down detector + timers between exercises and on unmount.
   *  Doesn't destroy the detector itself — that's reused across
   *  exercises within a route mount, mirroring the audio player. */
  const cleanupSession = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    detectorRef.current?.stop();
    eventsRef.current = [];
    submittedRef.current = false;
  }, []);

  // Reset when the exercise changes — we don't want a stale "you sang
  // do" carrying over into a new key.
  useEffect(() => {
    setPhase("idle");
    setReadout("");
    setErrorMessage(null);
    cleanupSession();
  }, [exercise.itemId, cleanupSession]);

  useEffect(() => {
    return () => {
      cleanupSession();
      detectorRef.current?.destroy();
      detectorRef.current = null;
    };
  }, [cleanupSession]);

  const handlePitch = useCallback(
    (event: PitchEvent) => {
      // Ring buffer — drop the oldest frames so detectHeldPitch's
      // walk-backward stays bounded.
      const buf = eventsRef.current;
      buf.push(event);
      if (buf.length > EVENT_WINDOW) buf.splice(0, buf.length - EVENT_WINDOW);

      if (submittedRef.current) return;
      const result = gradeVocalScaleDegree(buf, exercise);
      if (!result) return;

      // We've matched a scale tone — auto-submit.
      submittedRef.current = true;
      const syllables = mode === "minor" ? SOLFEGE_MINOR : SOLFEGE_MAJOR;
      const degree = parseInt(result.optionId.slice("degree-".length), 10);
      const syl = syllables[degree - 1] ?? `degree ${degree}`;
      const cents = Math.round(result.meanCents);
      const sign = cents > 0 ? "+" : "";
      setReadout(`heard: ${syl} (${sign}${cents}¢)`);
      setPhase("matched");
      cleanupSession();
      onAnswer(result.optionId);
    },
    [cleanupSession, exercise, mode, onAnswer],
  );

  const startSinging = useCallback(async () => {
    if (!audioPlayer) {
      setErrorMessage("Audio engine isn't ready yet.");
      return;
    }
    if (typeof tonicMidi !== "number") {
      // Defensive — exercise meta is missing the tonic context. Shouldn't
      // happen for scale-degree exercises post-generator-update.
      setErrorMessage("This exercise can't be graded by voice.");
      setPhase("error");
      return;
    }

    setErrorMessage(null);
    setReadout("");
    setPhase("playing-reference");

    // Lazily init the detector on first use, mirroring ensureAudio() in
    // Practice.tsx. The audio context for the detector is *not* the same
    // one Tone owns — Tone hides its context. We make a fresh one for
    // detection. Modern browsers cap to 6 AudioContexts per page, but
    // Earwise only ever holds two (Tone + here).
    let detector = detectorRef.current;
    if (!detector) {
      detector = createPitchDetector();
      detectorRef.current = detector;
      try {
        const ctx = new AudioContext();
        await detector.init(ctx, { workletUrl: pitchWorkletAssetUrl });
      } catch (err) {
        detectorRef.current = null;
        setPhase("error");
        setErrorMessage(
          `Couldn't initialize the pitch detector: ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
        return;
      }
    }

    // Play a short tonic reference. Build a one-note plan so it routes
    // through the same audio engine the question does — no chance of
    // a tuning mismatch between reference and target.
    const refBuilder = new AudioPlanBuilder();
    refBuilder.addNote(tonicMidi, TONIC_REF_SECONDS, 0.18, "target");
    refBuilder.advance(TONIC_REF_SECONDS + 0.2);
    await audioPlayer.play(refBuilder.build());

    // Move to listening. Subscribe BEFORE start() so the very first
    // pitch frame doesn't race a setState.
    eventsRef.current = [];
    submittedRef.current = false;
    unsubscribeRef.current = detector.onPitch(handlePitch);

    try {
      await detector.start();
    } catch (err) {
      const name = (err as { name?: string }).name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        setPhase("denied");
        setErrorMessage(
          "Microphone access was blocked. Re-enable it in your browser's site settings, then try again.",
        );
      } else {
        setPhase("error");
        setErrorMessage(
          `Couldn't start the microphone: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      cleanupSession();
      return;
    }

    setPhase("listening");
    silenceTimerRef.current = setTimeout(() => {
      if (submittedRef.current) return;
      setPhase("timeout");
      cleanupSession();
    }, SILENCE_TIMEOUT_MS);
  }, [audioPlayer, cleanupSession, handlePitch, tonicMidi]);

  // Unsupported exercise (e.g. interval-recognition in C.1) — render
  // nothing rather than a disabled button. Practice.tsx already gates
  // on exerciseType + vocalModeEnabled, but this is the defensive
  // belt-and-braces.
  if (typeof tonicMidi !== "number" || (mode !== "major" && mode !== "minor")) {
    return null;
  }

  const buttonDisabled =
    !enabled ||
    !audioPlayer ||
    phase === "playing-reference" ||
    phase === "listening" ||
    phase === "matched";
  const buttonLabel =
    phase === "playing-reference"
      ? "Playing reference…"
      : phase === "listening"
        ? "Listening…"
        : phase === "matched"
          ? "Submitting…"
          : phase === "timeout" || phase === "denied" || phase === "error"
            ? "Sing again"
            : "Sing the answer";

  return (
    <div className="mt-3 border border-ink/15 bg-paperWarm p-3">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => void startSinging()}
          disabled={buttonDisabled}
          aria-label={buttonLabel}
          className="px-4 py-2 bg-ink text-paper text-sm hover:bg-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {buttonLabel}
        </button>
        {readout && (
          <span
            className="font-mono text-xs text-inkSoft"
            aria-live="polite"
          >
            {readout}
          </span>
        )}
      </div>
      {phase === "timeout" && (
        <p className="mt-2 text-sm text-inkSoft font-serif italic" role="status">
          We didn&apos;t hear a clear pitch. Try again or pick a syllable to
          submit.
        </p>
      )}
      {errorMessage && (
        <p className="mt-2 text-sm text-danger font-serif italic" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
